"use client";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Howl } from "howler";
import { useKrpano } from "@/contexts/KrpanoProvider";
import type { KrpanoType } from "@/types/krpano";
import { useLanguage } from "@/contexts/I18nProvider";
import { useRouter } from "next/navigation";

declare global {
  function embedpano(config: {
    swf: string;
    xml: string;
    id: string;
    target: string;
    html5: string;
    mobilescale: number;
    consolelog?: boolean;
    debugmode?: boolean;
    passQueryParameters: boolean;
    initvars: { i18n_lang: string };
  }): void;
  interface Window {
    clickFloatGroup: (groupSector: string, sector: string) => void;
  }
}

const SECTOR_AUDIO_MAP: Record<string, string> = {
  'home_1': 'view-home',
  'home_2': 'view-home',
  'home_3': 'view-home',
  'home_4': 'view-home',
  'scene_smart_tourism_3d_2': 'view-home',
  'scene_smart_tourism_3d': 'view-tourism',
  'scene_smart_museum_3d': 'view-museum',
  'scene_smart_heritage_3d': 'view-heritage',
  'scene_smart_zone_3d': 'view-zone',
  'scene_smart_factory_3d': 'view-factory',
  'scene_smart_farm_3d': 'view-farm',
  'scene_smart_city_3d': 'view-city',
  'scene_smart_showroom_3d': 'view-showroom',
  'scene_smart_exhibition_3d': 'view-exhibition',
  'scene_smart_real_estate_3d': 'view-real-estate',
  'scene_smart_school_3d': 'view-school',
  'scene_smart_facility_3d': 'view-facility',
  'scene_smart_office_3d': 'view-office',
};

const KrpanoViewer = () => {
  const { krpano, setKrpano, krpanoCurrent, setKrpanoCurrent, sound, setSound, setUserInteracted, isTurnOnSound, setIsTurnOnSound } = useKrpano();
  const { locale } = useLanguage();
  const router = useRouter();
  const voiceMapRef = useRef<Record<string, any>>({});
  const [activeScene, setActiveScene] = useState<string>("");
  
  if (typeof window !== 'undefined') {
    window.clickFloatGroup = function (groupSector: string, sector: string) {
      const targetPath = `/${groupSector}/${sector}`;
      const localizedPath = locale && locale !== 'vn' ? `/${locale}${targetPath}` : targetPath;
      router.push(localizedPath);
    };
  }

  // Đăng ký sự kiện tương tác người dùng
  useEffect(() => {
    const handleInteraction = () => {
      setUserInteracted(true);
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };

    document.addEventListener('click', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);
    document.addEventListener('keydown', handleInteraction);

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [setUserInteracted]);

  useEffect(() => {
    const waitForKrpanoReady = (maxAttempts = 20, interval = 500) => {
      let attempts = 0;
      
      const checkKrpano = () => {
        attempts++;
        const krpanoInstance = document.getElementById("panorama");
        
        if (krpanoInstance && (krpanoInstance as unknown as KrpanoType).get) {
          try {
            const scene = (krpanoInstance as unknown as KrpanoType).get("xml.scene");
            if (scene) {
              setKrpano(krpanoInstance as unknown as KrpanoType);
              window.krpano = krpanoInstance as unknown as KrpanoType;
              return;
            }
          } catch (error) {
            console.log(`⏳ Krpano instance found but not fully ready, attempt ${attempts}/${maxAttempts}`);
          }
        } else {
          console.log(`⏳ Waiting for Krpano instance, attempt ${attempts}/${maxAttempts}`);
        }
        
        if (attempts < maxAttempts) {
          setTimeout(checkKrpano, interval);
        } else {
          console.error(`❌ Krpano failed to load after ${maxAttempts} attempts`);
        }
      };
      
      checkKrpano();
    };

    const loadKrpano = () => {
      if (typeof embedpano !== "function") return;

      console.log("🚀 Loading Krpano...");
      embedpano({
        swf: `${process.env.NEXT_PUBLIC_BASE_PATH_STATIC ? process.env.NEXT_PUBLIC_BASE_PATH_STATIC : ""}/krpano/tour.swf`,
        xml: `https://s3.hcm-1.cloud.cmctelecom.vn/${window.location.hostname === "platform.starglobal3d.com" ? "starglobal-3d" : "sg3d-demo"}/admin/krpano/tour-v1.xml`,
        id: "panorama",
        target: "pano",
        html5: "auto",
        mobilescale: 1.0,
        passQueryParameters: true,
        initvars: { i18n_lang: locale },
        consolelog: false,
        debugmode: false,
        webglsettings: { preserveDrawingBuffer: true },
      });

      waitForKrpanoReady();
    };

    if (typeof window !== "undefined") {
      const script = document.createElement("script");
      script.src = `${process.env.NEXT_PUBLIC_BASE_PATH_STATIC ? process.env.NEXT_PUBLIC_BASE_PATH_STATIC : ""}/krpano/tour.js`;
      script.onload = loadKrpano;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setKrpano]);

  // Tải dữ liệu voice từ API 1 lần duy nhất khi krpano sẵn sàng
  useEffect(() => {
    if (!krpano) return;
    axios("https://sanpham.starglobal3d.com/smart-starglobal-3d/starglobal/modules/information/php/scene_voice.php")
      .then((res) => {
        if (res.data) {
          voiceMapRef.current = res.data;
          const currentScene = String(krpano.get("xml.scene") || "");
          if (currentScene && res.data[currentScene]) {
            setKrpanoCurrent(res.data[currentScene]);
          }
        }
      })
      .catch((err) => console.error("Lỗi khi lấy dữ liệu âm thanh:", err));
  }, [krpano, setKrpanoCurrent]);

  // Theo dõi chuyển scene trong krpano tức thì
  useEffect(() => {
    if (!krpano) return;

    const checkScene = () => {
      const currentScene = String(krpano.get("xml.scene") || "");
      if (currentScene && currentScene !== activeScene) {
        setActiveScene(currentScene);
        if (voiceMapRef.current[currentScene]) {
          setKrpanoCurrent(voiceMapRef.current[currentScene]);
        }
      }
    };

    checkScene();
    const interval = setInterval(checkScene, 200);
    return () => clearInterval(interval);
  }, [krpano, activeScene, setKrpanoCurrent]);

  const activeSoundKeyRef = useRef<string>("");
  const currentHowlRef = useRef<Howl | null>(null);

  const stopAllAudios = () => {
    if (currentHowlRef.current) {
      try {
        currentHowlRef.current.stop();
        currentHowlRef.current.unload();
      } catch (e) {}
      currentHowlRef.current = null;
    }
    if (typeof window !== 'undefined' && (window as any).Howler) {
      try {
        (window as any).Howler.stop();
      } catch (e) {}
    }
    if (typeof document !== 'undefined') {
      document.querySelectorAll('audio').forEach(audio => {
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.removeAttribute('src');
          audio.load();
        } catch (e) {}
      });
    }
  };

  // Đăng ký helper toàn cục và object window.sound để khớp với lệnh js('sound.stop()') trong Krpano XML
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).sound = {
        stop: () => {
          stopAllAudios();
          activeSoundKeyRef.current = "";
        },
        unload: () => {
          stopAllAudios();
          activeSoundKeyRef.current = "";
        }
      };
      (window as any).stopAllKrpanoAudios = () => {
        stopAllAudios();
        activeSoundKeyRef.current = "";
      };
      (window as any).PlayThuyetminh = () => {
        stopAllAudios();
        activeSoundKeyRef.current = "";
        if (krpano) {
          const currentScene = String(krpano.get("xml.scene") || "");
          if (currentScene) {
            setActiveScene(currentScene);
          }
        }
      };
    }
  }, [krpano]);

  // Xử lý phát âm thanh duy nhất cho activeScene hiện tại
  useEffect(() => {
    if (krpano) {
      const currentLang = krpano.get("i18n_lang");
      if (currentLang !== locale) {
        krpano.set("i18n_lang", locale);
        krpano.set("langu", locale);
        const currentScene = krpano.get("xml.scene");
        if (currentScene) {
          krpano.call("loadscene(get(xml.scene), null, MERGE, KEEPVIEW);");
        }
      }
    }

    if (!isTurnOnSound || !activeScene) {
      stopAllAudios();
      activeSoundKeyRef.current = "";
      setSound(null);
      return;
    }

    const currentKey = `${activeScene}_${locale}`;
    if (activeSoundKeyRef.current === currentKey) {
      // Đã phát đúng sound cho scene + locale hiện tại, ngăn lặp lại âm thanh
      return;
    }

    // Tắt toàn bộ âm thanh cũ lập tức trước khi phát âm thanh mới
    stopAllAudios();

    activeSoundKeyRef.current = currentKey;

    const getAudioSources = (): string[] => {
      const lang = locale || 'en';
      const sceneData = voiceMapRef.current[activeScene] || krpanoCurrent;
      const sectorAudio = SECTOR_AUDIO_MAP[activeScene];

      if (lang === 'vn') {
        if (sceneData?.url_thuyetminh) {
          const vnUrl = sceneData.url_thuyetminh;
          return [vnUrl.startsWith('http') ? vnUrl : `https://sanpham.starglobal3d.com/smart-starglobal-3d${vnUrl}`];
        }
        if (sectorAudio) {
          return [`https://sanpham.starglobal3d.com/smart-starglobal-3d/upload/audio/vn/${sectorAudio}.mp3`];
        }
      }

      const langAudioProperty = `url_thuyetminh_${lang}`;
      const customLangAudio = sceneData ? sceneData[langAudioProperty] : undefined;

      if (customLangAudio) {
        const fullUrl = customLangAudio.startsWith('http')
          ? customLangAudio
          : `https://sanpham.starglobal3d.com/smart-starglobal-3d${customLangAudio}`;
        return [fullUrl];
      }

      // Tiếng Nhật (ja) trên CDN lưu định dạng file .wav
      if (lang === 'ja' && sectorAudio) {
        return [
          `https://sanpham.starglobal3d.com/smart-starglobal-3d/upload/audio/ja/${sectorAudio}.wav`,
          `https://sanpham.starglobal3d.com/smart-starglobal-3d/upload/audio/ja/${sectorAudio}.mp3`
        ];
      }

      // Tiếng Anh (en) hoặc các ngôn ngữ khác
      if (sectorAudio) {
        return [
          `https://sanpham.starglobal3d.com/smart-starglobal-3d/upload/audio/${lang}/${sectorAudio}.mp3`,
          `https://sanpham.starglobal3d.com/smart-starglobal-3d/upload/audio/${lang}/${sectorAudio}.wav`
        ];
      }

      if (sceneData?.url_thuyetminh_en) {
        let enUrl = sceneData.url_thuyetminh_en.replace(/\.mp4$/i, '.mp3');
        return [enUrl.startsWith('http') ? enUrl : `https://sanpham.starglobal3d.com/smart-starglobal-3d${enUrl}`];
      }

      return [];
    };

    const sources = getAudioSources();
    if (sources.length === 0) return;

    const newHowl = new Howl({
      src: sources,
      html5: false,
      volume: 1.0,
      autoplay: true,
      onload: () => {
        console.log('Đã tải âm thanh thành công:', sources[0]);
      },
      onloaderror: (_id, err) => {
        console.error('Lỗi khi tải âm thanh:', err, sources);
      },
      onplayerror: (_id, err) => {
        console.error('Lỗi khi phát âm thanh:', err, sources);
      }
    });

    currentHowlRef.current = newHowl;
    setSound(newHowl);
  }, [activeScene, isTurnOnSound, locale, setSound, krpanoCurrent]);

  return (
    <div id="pano" className="fixed top-0 left-0 w-full h-screen" />
  );
};

export default KrpanoViewer;


